import React from "react";
import { ArrowUpRight } from "lucide-react";
import BrandMark from "@/components/BrandMark";

export default function Footer() {
  const year = new Date().getFullYear();

  const scrollTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="relative border-t border-[rgba(10,10,10,0.08)] overflow-hidden"
    >
      <div className="section-container py-14 sm:py-16 relative">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-10">
          <div className="sm:col-span-1">
            <BrandMark size="sm" />
            <p className="mt-3 text-[13px] leading-[1.6] text-[#0a0a0a]/55 max-w-[260px]">
              A small studio that builds good websites for good businesses.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-[#0a0a0a]/65">
              <span className="status-dot" aria-hidden />
              <span className="font-medium text-[#0a0a0a]">Available</span>
              <span className="text-[#0a0a0a]/35">now — ships fast</span>
            </div>
          </div>

          <div>
            <div className="tick-label mb-4">Contact</div>
            <ul className="space-y-2.5 text-[13.5px]">
              <li>
                <a
                  href="mailto:hello@crestonagency.com"
                  className="group inline-flex items-center gap-1.5 text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                  data-testid="footer-email-link"
                >
                  hello@crestonagency.com
                  <ArrowUpRight className="size-3.5 opacity-0 group-hover:opacity-100 btn-arrow transition-opacity duration-150" />
                </a>
              </li>
              <li>
                <a
                  href="tel:+18452628024"
                  className="group inline-flex items-center gap-2 text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                  data-testid="footer-phone-link-mark"
                >
                  <span className="font-mono-mark text-[10.5px] tracking-[0.12em] uppercase text-[#0a0a0a]/45">
                    New projects
                  </span>
                  +1 (845) 262-8024
                  <ArrowUpRight className="size-3.5 opacity-0 group-hover:opacity-100 btn-arrow transition-opacity duration-150" />
                </a>
              </li>
              <li>
                <a
                  href="tel:+18482366560"
                  className="group inline-flex items-center gap-2 text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                  data-testid="footer-phone-link-joe"
                >
                  <span className="font-mono-mark text-[10.5px] tracking-[0.12em] uppercase text-[#0a0a0a]/45">
                    Support
                  </span>
                  +1 (848) 236-6560
                  <ArrowUpRight className="size-3.5 opacity-0 group-hover:opacity-100 btn-arrow transition-opacity duration-150" />
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="tick-label mb-4">Explore</div>
            <ul className="space-y-2.5 text-[13.5px]">
              <li>
                <a
                  href="#what"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .querySelector("#what")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                >
                  What we do
                </a>
              </li>
              <li>
                <a
                  href="#work"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .querySelector("#work")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                  data-testid="footer-link-work"
                >
                  Work
                </a>
              </li>
              <li>
                <a
                  href="#details"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .querySelector("#details")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                >
                  Details
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .querySelector("#contact")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="text-[#0a0a0a]/75 hover:text-[#0a0a0a] transition-colors duration-150"
                  data-testid="footer-link-contact"
                >
                  Get in touch
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Giant wordmark */}
        <div className="mt-14 sm:mt-20 relative">
          <div
            aria-hidden
            className="select-none font-sans-display font-black leading-[0.85] tracking-[-0.055em] text-[22vw] sm:text-[18vw] lg:text-[15vw] text-[#0a0a0a]/[0.06]"
          >
            creston<span className="text-[#facc15]/40">.</span>
          </div>
        </div>

        <div className="mt-6 sm:mt-8 pt-6 border-t border-[rgba(10,10,10,0.08)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[12.5px] text-[#0a0a0a]/50">
          <div>© {year} Creston. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <span className="font-mono-mark text-[11px] tracking-[0.12em] uppercase">
              Made with care in the U.S.
            </span>
            <button
              type="button"
              onClick={scrollTop}
              className="group inline-flex items-center gap-1.5 text-[#0a0a0a]/70 hover:text-[#0a0a0a] transition-colors duration-150"
              aria-label="Back to top"
            >
              Back to top
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[rgba(10,10,10,0.15)] group-hover:border-[#0a0a0a] group-hover:bg-[#0a0a0a] group-hover:text-[#fbf9f4] transition-colors duration-150">
                <ArrowUpRight className="size-3" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
